import { NavLink } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Network,
  UserSearch,
  Clock4,
  MessageSquareHeart,
  ShieldCheck,
  GitCompareArrows,
  ChevronLeft,
  ChevronRight,
  Activity,
  Route,
  Settings,
  BookOpen,
  Bell,
  Users,
  HelpCircle,
  PieChart,
  Calculator,
  BarChart3,
  LogOut,
} from "lucide-react";

const NAV_SECTIONS = [
  {
    label: "NAVEGACIÓN",
    items: [
      { to: "/", label: "Dashboard Ejecutivo", icon: LayoutDashboard, testid: "nav-dashboard" },
      { to: "/viaje", label: "Funnel Completo", icon: Route, testid: "nav-viaje" },
      { to: "/ambitos", label: "Análisis por Etapa", icon: Network, testid: "nav-ambitos" },
      { to: "/cohorte", label: "Cohorte", icon: PieChart, testid: "nav-cohorte" },
      { to: "/estimador", label: "Cotizador", icon: Calculator, testid: "nav-estimador" },
      { to: "/paciente", label: "Viaje Individual", icon: UserSearch, testid: "nav-paciente" },
      { to: "/contactabilidad", label: "Contactabilidad", icon: MessageSquareHeart, testid: "nav-contactabilidad" },
      { to: "/seguros", label: "Seguros y Planes", icon: ShieldCheck, testid: "nav-seguros" },
      { to: "/comparativa", label: "Comparativa", icon: GitCompareArrows, testid: "nav-comparativa" },
    ],
  },
  {
    label: "CONFIGURACIÓN",
    items: [
      { to: "/configuracion", label: "Configuración", icon: Settings, testid: "nav-configuracion" },
      { to: "/diccionario", label: "Diccionario de Datos", icon: BookOpen, testid: "nav-diccionario" },
      { to: "/alertas", label: "Alertas", icon: Bell, testid: "nav-alertas" },
      { to: "/usuarios", label: "Usuarios", icon: Users, testid: "nav-usuarios" },
      { to: "/analytics", label: "Estadísticas", icon: BarChart3, testid: "nav-analytics" },
    ],
  },
];

export default function Sidebar({ onNavigate }) {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();

  return (
    <aside
      data-testid="sidebar"
      className={`${
        collapsed ? "w-20" : "w-64"
      } border-r border-slate-200 bg-white/90 backdrop-blur-xl h-screen sticky top-0 flex flex-col transition-all duration-300 z-40`}
    >
      {/* Header */}
      <div className="h-16 px-5 flex items-center gap-3 border-b border-slate-200">
        <div
          className="h-9 w-9 rounded-xl flex items-center justify-center text-white shadow-md shadow-violet-300/40"
          style={{ background: "linear-gradient(135deg,#4c1d95,#7c3aed,#3b82f6)" }}
        >
          <Activity className="w-5 h-5" />
        </div>
        {!collapsed && (
          <div className="flex flex-col leading-tight">
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">UC CHRISTUS</span>
            <span className="text-sm font-bold text-slate-900">Inteligencia del Paciente</span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-5 overflow-y-auto">
        {NAV_SECTIONS.map((section) => (
          <div key={section.label}>
            {!collapsed && (
              <div className="px-3 mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                {section.label}
              </div>
            )}
            <div className="space-y-1">
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={item.testid}
                  title={collapsed ? item.label : undefined}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      isActive
                        ? "bg-gradient-to-r from-violet-600 to-blue-500 text-white shadow-md shadow-violet-300/40"
                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                    }`
                  }
                >
                  <item.icon className="w-[18px] h-[18px] shrink-0" />
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Centro de Ayuda */}
      <div className="px-3 pb-2">
        <NavLink
          to="/ayuda"
          data-testid="nav-ayuda"
          className={({ isActive }) =>
            `flex items-center ${collapsed ? "justify-center" : "gap-3"} px-3 py-2.5 rounded-xl text-sm font-medium transition ${
              isActive
                ? "bg-gradient-to-r from-violet-600 to-blue-500 text-white shadow-md"
                : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            }`
          }
          title={collapsed ? "Centro de Ayuda" : undefined}
        >
          <HelpCircle className="w-[18px] h-[18px] shrink-0" />
          {!collapsed && <span>Centro de Ayuda</span>}
        </NavLink>
      </div>

      {/* User / Logout */}
      <div className="px-3 pb-2">
        <div className="flex items-center gap-3 px-3 py-2 text-sm">
          {!collapsed && (
            <span className="text-xs text-slate-500 truncate">{user?.full_name}</span>
          )}
          <button
            onClick={logout}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-rose-600 transition ml-auto"
            title="Cerrar sesión"
            aria-label="Cerrar sesión"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Collapse toggle */}
      <div className="p-3 border-t border-slate-200">
        <button
          data-testid="sidebar-toggle"
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-100 transition"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <><ChevronLeft className="w-4 h-4" /> Colapsar</>}
        </button>
      </div>
    </aside>
  );
}
